/* eslint-disable react/require-default-props */
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Modal from '../../../../../components/Modal/Modal';
import {
   PrimaryButton,
   GhostButton,
   SmallButton,
} from '../../../../../styles/ui/Button';
import {
   listCandidates,
   pullCandidateToOpening,
} from '../../../../../services/hr/recruitment/candidatesApi';
import { formatDateBr } from '../../utils/format';
import { recruitmentErrorMessage } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// Atalho da ficha da vaga: puxar candidatos do BANCO DE CURRÍCULOS direto pra
// vaga, sem abrir a ficha de cada um. Cards resumidos (nome / cargo pretendido
// / data de cadastro) com seleção MÚLTIPLA; o confirmar puxa um a um
// (PATCH /candidates/:id/opening) e reporta falhas individualmente — sucesso
// parcial mantém o modal aberto com o banco recarregado. Busca explícita
// (Enter/botão), como toda pesquisa do módulo.
// ---------------------------------------------------------------------------

const POOL_PAGE_SIZE = 100;

const SearchRow = styled.div`
   display: flex;
   gap: 8px;
   margin-bottom: 12px;

   input {
      flex: 1;
      min-width: 0;
      min-height: 32px;
      padding: 6px 10px;
      border-radius: 8px;
      border: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
      font-size: 0.85rem;
      outline: none;

      &:focus {
         border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
         box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
      }
   }
`;

// Scroll só quando a lista passa de ~5 cards (feedback 2026-07-14): a altura
// limite corresponde a 5 cards de duas linhas + gaps.
const CandidateList = styled.div`
   display: grid;
   gap: 8px;
   max-height: ${({ $scrollable }) => ($scrollable ? '350px' : 'none')};
   overflow-y: ${({ $scrollable }) => ($scrollable ? 'auto' : 'visible')};
   padding-right: 2px;
`;

// <button> real (foco/teclado de graça — G.6); $selected = destaque azul.
const CandidateCard = styled.button`
   width: 100%;
   text-align: left;
   background: ${({ $selected }) =>
      $selected ? 'rgba(24,95,165,0.06)' : '#fff'};
   border: 1px solid
      ${({ $selected }) =>
         $selected ? 'rgba(24,95,165,0.45)' : 'rgba(0,0,0,0.12)'};
   border-radius: 12px;
   padding: 10px 14px;
   cursor: pointer;

   display: grid;
   gap: 4px;

   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.3);
   }
   &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
   }

   .card-name {
      font-weight: 700;
      font-size: 0.92rem;
      color: #1f1f18;
      display: flex;
      align-items: center;
      gap: 8px;
   }
   .card-check {
      margin-left: auto;
      font-weight: 800;
      color: #0c447c;
   }
   .card-sub {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      font-size: 0.8rem;
      color: #6b7280;
   }
`;

const HintText = styled.p`
   margin: 0 0 12px;
   font-size: 13px;
   color: #555;
`;

// Mesmo fallback de rótulo do PullToOpeningModal (título → cargo → #id).
function openingLabel(opening) {
   const position = opening?.job_position?.position_name;
   return opening?.title || position || `Vaga #${opening?.id}`;
}

export default function PullCandidatesModal({
   isOpen,
   onClose,
   opening,
   onSaved,
}) {
   const [pool, setPool] = useState([]);
   const [poolTotal, setPoolTotal] = useState(0);
   const [isLoadingPool, setIsLoadingPool] = useState(false);
   const [searchDraft, setSearchDraft] = useState('');
   // id → candidato (objeto, não Set de ids): a seleção sobrevive a novas
   // buscas — dá pra selecionar em buscas diferentes e puxar todos de uma vez.
   const [selectedById, setSelectedById] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);

   const fetchPool = useCallback(async (search) => {
      setIsLoadingPool(true);
      try {
         const result = await listCandidates({
            onlyPool: true,
            search: search || undefined,
            perPage: POOL_PAGE_SIZE,
         });
         setPool(result.candidates ?? []);
         setPoolTotal(result.total ?? 0);
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(
               err,
               'Erro ao carregar o banco de currículos.',
            ),
         );
      } finally {
         setIsLoadingPool(false);
      }
   }, []);

   // Abertura do modal: zera estado e carrega o banco.
   useEffect(() => {
      if (!isOpen) return;
      setSearchDraft('');
      setSelectedById({});
      setIsSubmitting(false);
      fetchPool('');
   }, [isOpen, fetchPool]);

   const toggleSelected = (candidate) => {
      setSelectedById((prev) => {
         const next = { ...prev };
         if (next[candidate.id]) delete next[candidate.id];
         else next[candidate.id] = candidate;
         return next;
      });
   };

   const handleSearch = () => {
      if (isLoadingPool || isSubmitting) return;
      fetchPool(searchDraft.trim());
   };

   const handleConfirm = async () => {
      const targets = Object.values(selectedById);
      if (isSubmitting || targets.length === 0) return;

      setIsSubmitting(true);
      let successCount = 0;
      const failures = [];

      // Sequencial de propósito: cada puxada passa pelos guards do backend
      // (vaga aberta, stage certo) e reporta falha por candidato.
      // eslint-disable-next-line no-restricted-syntax
      for (const candidate of targets) {
         try {
            // eslint-disable-next-line no-await-in-loop
            await pullCandidateToOpening(candidate.id, Number(opening.id));
            successCount += 1;
         } catch (err) {
            failures.push({
               name: candidate.full_name,
               message: recruitmentErrorMessage(
                  err,
                  'Erro ao puxar o candidato.',
               ),
            });
         }
      }

      if (successCount > 0) {
         toast.success(
            successCount === 1
               ? '1 candidato puxado para a vaga.'
               : `${successCount} candidatos puxados para a vaga.`,
         );
         onSaved?.();
      }
      failures.forEach((failure) => {
         toast.error(`${failure.name}: ${failure.message}`);
      });

      setIsSubmitting(false);
      if (failures.length === 0) {
         onClose?.();
      } else {
         // Sucesso parcial: recarrega o banco (os puxados saem do pool) e
         // mantém o modal aberto pro operador resolver o restante.
         setSelectedById({});
         fetchPool(searchDraft.trim());
      }
   };

   const selectedCount = Object.keys(selectedById).length;
   const confirmLabel =
      selectedCount <= 1
         ? 'Puxar candidato'
         : `Puxar ${selectedCount} candidatos`;

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title="Puxar candidatos para a vaga"
         subtitle={opening ? openingLabel(opening) : ''}
         maxWidth="560px"
         footer={
            <>
               <GhostButton
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
               >
                  Cancelar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting || selectedCount === 0}
               >
                  {isSubmitting ? 'Puxando…' : confirmLabel}
               </PrimaryButton>
            </>
         }
      >
         <HintText>
            Selecione candidatos do <strong>banco de currículos</strong> — cada
            um entra na esteira desta vaga na <strong>Entrevista 1</strong>.
         </HintText>

         <SearchRow>
            <input
               type="text"
               placeholder="Buscar por nome…"
               value={searchDraft}
               onChange={(e) => setSearchDraft(e.target.value)}
               onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                     e.preventDefault();
                     handleSearch();
                  }
               }}
               disabled={isSubmitting}
            />
            <SmallButton
               type="button"
               $intent="cta"
               onClick={handleSearch}
               disabled={isLoadingPool || isSubmitting}
            >
               {isLoadingPool ? 'Buscando…' : 'Buscar'}
            </SmallButton>
         </SearchRow>

         {pool.length === 0 ? (
            <HintText style={{ margin: 0 }}>
               {isLoadingPool
                  ? 'Carregando o banco de currículos…'
                  : 'Nenhum candidato disponível no banco de currículos.'}
            </HintText>
         ) : (
            <>
               <CandidateList $scrollable={pool.length > 5}>
                  {pool.map((candidate) => {
                     const isSelected = Boolean(selectedById[candidate.id]);
                     return (
                        <CandidateCard
                           key={candidate.id}
                           type="button"
                           $selected={isSelected}
                           aria-pressed={isSelected}
                           disabled={isSubmitting}
                           onClick={() => toggleSelected(candidate)}
                        >
                           <div className="card-name">
                              {candidate.full_name}
                              {isSelected && (
                                 <span className="card-check">✓</span>
                              )}
                           </div>
                           <div className="card-sub">
                              <span>
                                 {candidate.desired_position?.position_name ??
                                    'Cargo não informado'}
                              </span>
                              <span>
                                 Cadastro: {formatDateBr(candidate.created_at)}
                              </span>
                           </div>
                        </CandidateCard>
                     );
                  })}
               </CandidateList>
               {poolTotal > pool.length && (
                  <HintText style={{ margin: '10px 0 0' }}>
                     Mostrando {pool.length} de {poolTotal} — refine a busca
                     para encontrar os demais.
                  </HintText>
               )}
            </>
         )}
      </Modal>
   );
}

PullCandidatesModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   opening: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      job_position: PropTypes.shape({
         position_name: PropTypes.string,
      }),
   }),
   onSaved: PropTypes.func,
};
